import React, { Fragment, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Grid,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
    styled,
    useTheme,
} from '@mui/material';

import { useAppDispatch, useAppSelector } from '../../state/hooks';
import {
    fetchInboundOrders,
    fetchOutboundOrders,
} from '../../state/features/laneDetailSlice';
import { fetchAreas } from '../../state/features/mapSlice';
import { ApiStatus, EquipmentTypeCode } from '../../utils/enums';
import { formatCurrency, formatNumber } from '../../utils/formattingUtils';
import { buildAreaLink } from '../../utils/navigationHelpers';
import { useFilters } from '../Filters/useFilters';
import Revenue from '../Widgets/Revenue';
import AverageRate from '../Widgets/AverageRate';
import Transit from '../Widgets/Transit';
import LaneDetailsMap from '../Map/LaneDetailsMap';
import Performance from '../Widgets/Performance';
import Volume from '../Widgets/Volume';
import BalanceLabel from '../Labels/BalanceLabel';

const classesPrefix = 'laneDetails';

const classes = {
    statContainer: `${classesPrefix}-statContainer`,
    revenueContainer: `${classesPrefix}-revenueContainer`,
    rateContainer: `${classesPrefix}-rateContainer`,
    volumeContainer: `${classesPrefix}-volumeContainer`,
    balanceContainer: `${classesPrefix}-balanceContainer`,
    table: `${classesPrefix}-table`,
    tableContainer: `${classesPrefix}-tableContainer`,
    mapContainer: `${classesPrefix}-mapContainer`,
};

const StyledGrid = styled(Grid)(({ theme }) => {
    return {
        [`& .${classes.statContainer}`]: {
            padding: theme.spacing(2),
            marginBottom: 0,
            display: 'flex',
            alignItems: 'stretch',
            order: 1,
        },
        [`& .${classes.revenueContainer}`]: {
            order: 1,
        },
        [`& .${classes.rateContainer}`]: {
            [theme.breakpoints.down('lg')]: {
                order: 3,
            },
        },
        [`& .${classes.volumeContainer}`]: {
            [theme.breakpoints.down('lg')]: {
                order: 3,
            },
        },
        [`& .${classes.balanceContainer}`]: {
            display: 'flex',
            justifyContent: 'flex-end',
        },
        [`& .${classes.table}`]: {
            '& td': {
                borderBottom: 'none',
                paddingLeft: 0,
                paddingRight: 0,
            },
        },
        [`& .${classes.tableContainer}`]: {
            display: 'flex',
            alignItems: 'flex-end',
        },
        [`& .${classes.mapContainer}`]: {
            display: 'flex',
            flexDirection: 'column',
            order: 2,
            [theme.breakpoints.down('lg')]: {
                order: 1,
            },
        },
    };
});

const LaneDetails = ({
    equipmentType,
}: {
    equipmentType: EquipmentTypeCode;
}): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const filters = useFilters();

    const origin = searchParams.get('origin');
    const dest = searchParams.get('dest');

    const inboundOrdersApiStatus = useAppSelector((state) => { return state.lane.inboundOrdersApiStatus; });
    const inboundOrders = useAppSelector((state) => { return state.lane.inboundOrders; });

    const outboundOrdersApiStatus = useAppSelector((state) => { return state.lane.outboundOrdersApiStatus; });
    const outboundOrdersCount = useAppSelector((state) => { return state.lane.outboundOrdersCount; });

    const laneDetailsApiStatus = useAppSelector((state) => { return state.lane.laneDetailsApiStatus; });
    const laneDetails = useAppSelector((state) => { return state.lane.laneDetails; });

    const areasApiStatus = useAppSelector((state) => { return state.map.areasApiStatus; });
    const areas = useAppSelector((state) => { return state.map.areas; });

    /**
     * check if a lane has the same origin and destination
     * @returns true if origin == destination
     */
    const isIntraAreaLane = useMemo(() => {
        return origin === dest;
    }, [origin, dest]);

    useEffect(() => {
        dispatch(fetchAreas({
            origin: origin as string,
            destination: dest as string,
        }));
    }, [dispatch, origin, dest]);

    useEffect(() => {
        dispatch(fetchInboundOrders({
            pickup_service_area_id: origin as string,
            delivery_service_area_id: dest as string,
            mri_freight_order_specified_eqpt_code: equipmentType,
            filter_criteria: filters,
        }));
    }, [dispatch, origin, dest, equipmentType, filters]);

    useEffect(() => {
        if (!isIntraAreaLane) {
            dispatch(fetchOutboundOrders({
                pickup_service_area_id: dest as string,
                delivery_service_area_id: origin as string,
                filter_criteria: filters,
            }));
        }
    }, [dispatch, isIntraAreaLane, origin, dest, filters]);

    const handleAreaClick = (areaId: string): void => {
        navigate(buildAreaLink(areaId, filters));
    };

    const determineBalanceAmount = (): number => {
        if (isIntraAreaLane) {
            return 0;
        }

        const inboundOrdercount = laneDetails.freight_order_count ?? 0;
        return inboundOrdercount - outboundOrdersCount;
    };

    const renderInboundLaneTitle = (): React.JSX.Element => {
        return (
            <Fragment>
                <small>From </small>
                <strong>
                    {laneDetails.pickup_service_area_name || laneDetails.pickup_service_area_id || origin}
                </strong>
                <small> to </small>
                <strong>
                    {laneDetails.delivery_service_area_name || laneDetails.delivery_service_area_id || dest}
                </strong>
            </Fragment>
        );
    };

    const renderOutboundLaneTitle = (): React.JSX.Element => {
        return (
            <Fragment>
                <small>From </small>
                <strong>
                    {laneDetails.delivery_service_area_name || laneDetails.delivery_service_area_id || dest}
                </strong>
                <small> to </small>
                <strong>
                    {laneDetails.pickup_service_area_name || laneDetails.pickup_service_area_id || origin}
                </strong>
            </Fragment>
        );
    };

    return (
        <StyledGrid container>
            <Grid container>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    lg
                    className={`${classes.statContainer} ${classes.revenueContainer}`}
                >
                    <Revenue
                        loading={laneDetailsApiStatus === ApiStatus.Idle || laneDetailsApiStatus === ApiStatus.Loading}
                        totalRevenue={laneDetails.total_revenue_usd_amount}
                        linehaulRevenue={laneDetails.linehaul_revenue_usd_amount}
                        fuelRevenue={laneDetails.fuel_surcharge_usd_amount}
                        accessorialRevenue={laneDetails.accessorial_revenue_usd_amount}
                        orderVolume={laneDetails.freight_order_count}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    lg
                    className={`${classes.statContainer} ${classes.rateContainer}`}
                >
                    <AverageRate
                        loading={laneDetailsApiStatus === ApiStatus.Idle || laneDetailsApiStatus === ApiStatus.Loading}
                        totalRevenue={laneDetails.total_revenue_usd_amount}
                        linehaulRevenue={laneDetails.linehaul_revenue_usd_amount}
                        fuelRevenue={laneDetails.fuel_surcharge_usd_amount}
                        accessorialRevenue={laneDetails.accessorial_revenue_usd_amount}
                        loadedMiles={laneDetails.loaded_distance_miles_amount}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    lg
                    className={`${classes.statContainer} ${classes.volumeContainer}`}
                >
                    <Grid
                        container
                        direction='column'
                        sx={{
                            alignItems: 'stretch',
                        }}
                    >
                        <Grid item xs>
                            <Volume
                                loading={laneDetailsApiStatus === ApiStatus.Idle || laneDetailsApiStatus === ApiStatus.Loading}
                                orderVolume={laneDetails.freight_order_count}
                            />
                        </Grid>
                        <Grid item xs='auto'>
                            <StyledGrid container direction='column' justifyContent='flex-end'>
                                <Grid item xs='auto'>
                                    <Typography variant='h4'>
                                        Lane Balance
                                    </Typography>
                                </Grid>
                                <Grid item xs className={classes.balanceContainer}>
                                    <BalanceLabel
                                        apiStatus={isIntraAreaLane ? laneDetailsApiStatus : outboundOrdersApiStatus}
                                        balanceType='lane'
                                        isDailyBalance={false}
                                        balanceAmount={determineBalanceAmount()}
                                    />
                                </Grid>
                                <Grid item xs='auto' className={classes.tableContainer}>
                                    <Table className={classes.table} size='small'>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>Avg. Lane Yield</TableCell>
                                                <TableCell align='right'>
                                                    {
                                                        laneDetailsApiStatus === ApiStatus.Idle || laneDetailsApiStatus === ApiStatus.Loading ? (
                                                            <Skeleton />
                                                        ) : (
                                                            formatCurrency(laneDetails.lane_yield_usd, 0, 0)
                                                        )
                                                    }
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>{renderInboundLaneTitle()}</TableCell>
                                                <TableCell align='right'>
                                                    {
                                                        laneDetailsApiStatus === ApiStatus.Idle || laneDetailsApiStatus === ApiStatus.Loading ? (
                                                            <Skeleton />
                                                        ) : (
                                                            formatNumber(laneDetails.freight_order_count, 0, 0)
                                                        )
                                                    }
                                                </TableCell>
                                            </TableRow>
                                            {
                                                !isIntraAreaLane &&
                                                <TableRow>
                                                    <TableCell>{renderOutboundLaneTitle()}</TableCell>
                                                    <TableCell align='right'>
                                                        {
                                                            outboundOrdersApiStatus === ApiStatus.Idle || outboundOrdersApiStatus === ApiStatus.Loading ?
                                                                (
                                                                    <Skeleton />
                                                                ) : (
                                                                    formatNumber(outboundOrdersCount, 0, 0)
                                                                )
                                                        }
                                                    </TableCell>
                                                </TableRow>
                                            }
                                        </TableBody>
                                    </Table>
                                </Grid>
                            </StyledGrid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    lg={4}
                    className={classes.mapContainer}
                >
                    <LaneDetailsMap
                        isFetchingData={
                            laneDetailsApiStatus === ApiStatus.Idle || laneDetailsApiStatus === ApiStatus.Loading ||
                            areasApiStatus === ApiStatus.Idle || areasApiStatus === ApiStatus.Loading
                        }
                        theme={theme}
                        lanes={[laneDetails]}
                        areas={areas}
                        handleAreaClick={handleAreaClick}
                    />
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={12} md lg={8} className={classes.statContainer}>
                    <Transit
                        loading={laneDetailsApiStatus === ApiStatus.Idle || laneDetailsApiStatus === ApiStatus.Loading}
                        orderVolume={laneDetails.freight_order_count}
                        transitHours={laneDetails.first_stop_departure_to_final_stop_arrival_hours_duration}
                        dwellTime={laneDetails.netwise_dwell_hours_duration}
                        inTransitDelayHours={laneDetails.netwise_in_transit_delay_hours_duration}
                        emptyMiles={laneDetails.empty_distance_miles_amount}
                        loadedMiles={laneDetails.loaded_distance_miles_amount}
                        loadTime={laneDetails.load_hours_duration}
                        unloadTime={laneDetails.unload_hours_duration}
                        totalRevenue={laneDetails.total_revenue_usd_amount}
                        totalMiles={laneDetails.travel_distance_miles_amount}
                        emptyHours={laneDetails.empty_transit_hours_duration}
                    />
                </Grid>
                <Grid item xs={12} md lg={4} className={classes.statContainer}>
                    <Performance
                        orders={inboundOrders}
                        loadingTopShippers={inboundOrdersApiStatus === ApiStatus.Idle || inboundOrdersApiStatus === ApiStatus.Loading}
                        inboundId={laneDetails.pickup_service_area_id}
                        outboundId={laneDetails.delivery_service_area_id}
                    />
                </Grid>
            </Grid>
        </StyledGrid>
    );
};

export default LaneDetails;
